'use client';

import axios from 'axios';
import moment from 'moment-timezone';
import { useToast } from '@/app/context/toast-provider';
import { SUCCESS_MESSAGES } from '@/app/utils/errorMessage';
import { useConfirmDialog } from '../useConfirmDialog/useConfirmDialog';
/** The candidate's AI interview URL: FastRTC rooms use the v2 path, Daily rooms the token query. */
export const buildScreeningLink = (origin, companySlug, screeningInterview) => screeningInterview?.interviewTransport === 'FASTRTC' ? `${origin}/${companySlug}/career/screening-v2/${screeningInterview?.roomToken}` : `${origin}/${companySlug}/career/screeningInterview?token=${screeningInterview?.roomToken}`;

/**
 * Copying and emailing the AI interview link. Sending asks first (showing the address and expiry) and is refused
 * while question edits are unsaved; it calls the reminder endpoint, which emails (and WhatsApps, when on) the link.
 */
export function useScreeningLink({
  interview,
  companySlug,
  getInterviewData,
  refreshJobApplicationList,
  setRequestLoading,
  hasUnsavedQuestions = false,
  onSent
}) {
  const {
    confirm
  } = useConfirmDialog();
  const {
    showErrorToast,
    showSuccessToast
  } = useToast();
  const handleCopyScreeningLink = () => {
    navigator.clipboard.writeText(buildScreeningLink(window.location.origin, companySlug, interview?.ScreeningInterview));
    showSuccessToast(SUCCESS_MESSAGES.COPIED_TO_CLIPBOARD);
  };
  const handleSendScreeningLink = async () => {
    setRequestLoading?.(true);
    try {
      const response = await axios.get(`/api/interviews/screeningInterview/remind?interviewId=${interview?.id}`);
      const {
        message
      } = response.data;
      showSuccessToast(message || SUCCESS_MESSAGES.SCREENING_LINK_SENT);
      // Refresh interview data so interviewEvents (link_sent) is reflected immediately
      await getInterviewData();
      refreshJobApplicationList?.();
      onSent?.();
    } catch (error) {
      const errMessage = error?.response?.data?.message || error?.response?.data?.error || 'Something went wrong';
      showErrorToast(errMessage);
      throw error;
    } finally {
      setRequestLoading?.(false);
    }
  };
  const handleSendLinkClick = () => {
    if (hasUnsavedQuestions) {
      showErrorToast('Please save your changes before sending the link.');
      return;
    }
    confirm({
      title: 'Send AI Screening Link',
      maxWidth: 'xs',
      confirmButtonText: 'Send Link',
      color: 'primary',
      content: <div className="flex flex-col gap-4">
                    <div className="overflow-hidden rounded-lg border border-border">
                        <table className="w-full text-sm">
                            <tbody className="divide-y divide-gray-100">
                                <tr className="bg-page">
                                    <th scope="row" className="bg-muted px-3 py-2.5 text-left font-medium text-foreground-muted">
                                        Email
                                    </th>
                                    <td className="px-3 py-2.5 text-foreground">{interview?.email || '—'}</td>
                                </tr>
                                {interview?.ScreeningInterview?.expiresAt && <tr className="bg-page">
                                        <th scope="row" className="bg-muted px-3 py-2.5 text-left font-medium text-foreground-muted">
                                            Link expires
                                        </th>
                                        <td className="px-3 py-2.5 text-foreground">
                                            {moment(interview.ScreeningInterview.expiresAt).format('MMMM DD, YYYY hh:mm A')}
                                        </td>
                                    </tr>}
                            </tbody>
                        </table>
                    </div>
                    <div className="bg-muted rounded-lg p-3">
                        <p className="text-sm text-foreground mb-2">
                            <strong>Note:</strong>
                        </p>
                        <p className="text-sm text-foreground"> The candidate will receive an email with:</p>
                        <ul className="text-sm text-foreground-muted mt-1 ml-4 list-disc space-y-1">
                            <li>Link to start the AI interview</li>
                            <li>Instructions for the interview process</li>
                            <li>Important reminders about interview requirements</li>
                        </ul>
                    </div>
                </div>,
      onConfirm: handleSendScreeningLink
    });
  };
  return {
    handleCopyScreeningLink,
    handleSendScreeningLink,
    handleSendLinkClick
  };
}